import React, { FC } from 'react';
import { CloseOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Input, Button, Select } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import {
	choiceLevelFollowUpActionsExist,
	FOLLOW_UP_ACTION_OPTIONS,
	isTaskListEnabled,
	SITE_SURVEY_ANSWER_TYPE,
	taskLevelFollowUpActionsExist,
} from '../../utils/SiteSurveyUtils';
import './AddTask.less';

// Task-level and per-option follow-up actions are mutually exclusive — a task
// uses one level or the other, never both. These messages explain which control
// is locked and how to unlock it, mirroring the wording in the import grid's
// FollowUpActionsField / MultipleChoicesField.
const TASK_LEVEL_LOCKED_MESSAGE =
	'Follow-up actions are set on individual answer options. Clear them to set task-level actions.';
const OPTION_LEVEL_LOCKED_MESSAGE =
	'Follow-up actions are set at the task level. Clear them to set per-option actions.';

const formItemLayout = {
	labelCol: {
		xs: { span: 24 },
		sm: { span: 4 },
	},
	wrapperCol: {
		xs: { span: 24 },
		sm: { span: 20 },
	},
};

const formItemLayoutWithOutLabel = {
	wrapperCol: {
		xs: { span: 24, offset: 0 },
		sm: { span: 20, offset: 4 },
	},
};

interface IAddTask {
	taskKeys: any[];
	taskKey: any;
	taskIndex: number;
	form: WrappedFormUtils<any>;
	formData: any;
	isSingleAddition?: boolean;
	removeTask: (k: any) => void;
	updateChoiceKeys: (k: any, val: any, opt: DefaultOptionType | DefaultOptionType[]) => void;
	addChoice: (k: any) => void;
	removeChoice: (k: any, c: any) => void;
}

const AddTask: FC<IAddTask> = ({
	taskKeys,
	taskKey,
	taskIndex,
	form,
	formData,
	isSingleAddition,
	removeTask,
	updateChoiceKeys,
	addChoice,
	removeChoice,
}): React.ReactElement => {
	const taskType = nullSafeGet(`tasks.${taskKey}.taskType`, formData);
	const taskChoices = nullSafeGet(`tasks.${taskKey}.multipleChoicesWithLabel`, formData);
	const taskChecklistItems = nullSafeGet(`tasks.${taskKey}.checklistTasks`, formData);
	const sourceItemsForKeys =
		taskType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST ? taskChecklistItems : taskChoices;
	const itemsBackedDefaultKeys =
		sourceItemsForKeys && sourceItemsForKeys.length > 0
			? sourceItemsForKeys.map((el, idx) => idx)
			: [0, 1];
	const defaultChoiceKeys =
		taskType === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE ||
		taskType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST
			? itemsBackedDefaultKeys
			: [];
	form.getFieldDecorator(`tasks-${taskKey}-choiceKeys`, {
		initialValue: nullSafeGetOrElse(`tasks-${taskKey}-choiceKeys`, formData, defaultChoiceKeys),
	});

	// Mutual exclusion between task-level and per-option follow-up actions. We read
	// the live form values (the form re-renders on every field change, so these
	// stay current) and fall back to the initial formData on the first render —
	// before each field's decorator has registered, getFieldValue returns
	// undefined, so the fallback keeps an edited template's existing follow-ups
	// from being missed on mount.
	const isMultipleChoice =
		form.getFieldValue(`tasks-${taskKey}-type`) === SITE_SURVEY_ANSWER_TYPE.MULTIPLE_CHOICE;
	const choiceKeysForFollowUps = form.getFieldValue(`tasks-${taskKey}-choiceKeys`) || [];
	const choicesWithFollowUps = isMultipleChoice
		? choiceKeysForFollowUps.map((choiceKey) => {
				const liveValue = form.getFieldValue(
					`tasks-${taskKey}-choices-${choiceKey}-followUpActions`
				);
				const followUpActions =
					liveValue !== undefined
						? liveValue
						: nullSafeGetOrElse(
								`tasks.${taskKey}.multipleChoicesWithLabel.${choiceKey}.followUpActions`,
								formData,
								[]
						  );
				return { followUpActions };
		  })
		: [];
	const optionLevelFollowUpsSet = choiceLevelFollowUpActionsExist(choicesWithFollowUps);

	const liveTaskFollowUps = form.getFieldValue(`tasks-${taskKey}-followUpActions`);
	const taskLevelFollowUpsValue =
		liveTaskFollowUps !== undefined
			? liveTaskFollowUps
			: nullSafeGetOrElse(`tasks.${taskKey}.followUpActions`, formData, []);
	const taskLevelFollowUpsSet = taskLevelFollowUpActionsExist(taskLevelFollowUpsValue);

	return (
		<div key={taskKey} className="task-container">
			{!isSingleAddition && (
				<div style={{ display: 'flex', justifyContent: 'space-between' }}>
					<div style={{ fontSize: '16px', color: 'black', marginBottom: '24px' }}>
						Step {taskIndex + 1}
					</div>

					<div className="remove-task-button">
						{taskKeys.length > 1 ? (
							<CloseOutlined
								className="dynamic-delete-button"
								onClick={() => removeTask(taskKey)}
							/>
						) : null}
					</div>
				</div>
			)}
			<Form.Item {...formItemLayout} label="Title" required={false}>
				{form.getFieldDecorator(`tasks-${taskKey}-title`, {
					validateTrigger: ['onChange', 'onBlur'],
					initialValue:
						nullSafeGet(`tasks-${taskKey}-title`, formData) ||
						nullSafeGet(`tasks.${taskKey}.title`, formData),
					rules: [
						{
							required: true,
							whitespace: true,
							message: 'Please give your task a title.',
						},
						{
							message: 'Title already exists under same group.',
							validator: (rule, value, callback) => {
								const getUniqueKey = (key) =>
									`${form.getFieldValue(`tasks-${key}-groupTitle`)}-${form.getFieldValue(
										`tasks-${key}-title`
									)}`;
								const allKeys = taskKeys
									.filter((_) => _ !== taskKey)
									.map((key) => getUniqueKey(key));
								const currentKey = getUniqueKey(taskKey);
								if (allKeys.includes(currentKey)) {
									callback('error');
									return Promise.reject('');
								}
								return Promise.resolve();
							},
						},
					],
				})(<Input style={{ width: '60%' }} />)}
			</Form.Item>
			<Form.Item {...formItemLayout} label="Description" required={false}>
				{form.getFieldDecorator(`tasks-${taskKey}-description`, {
					validateTrigger: ['onChange', 'onBlur'],
					initialValue:
						nullSafeGet(`tasks-${taskKey}-description`, formData) ||
						nullSafeGet(`tasks.${taskKey}.description`, formData),
					rules: [
						{
							required: false,
							whitespace: true,
						},
					],
				})(<Input.TextArea style={{ width: '60%' }} autoSize={{ minRows: 1, maxRows: 4 }} />)}
			</Form.Item>
			<Form.Item {...formItemLayout} label="Group title" required={false}>
				{form.getFieldDecorator(`tasks-${taskKey}-groupTitle`, {
					validateTrigger: ['onChange', 'onBlur'],
					initialValue:
						nullSafeGet(`tasks-${taskKey}-groupTitle`, formData) ||
						nullSafeGet(`tasks.${taskKey}.groupTitle`, formData),
					rules: [
						{
							required: false,
							whitespace: true,
						},
					],
				})(<Input style={{ width: '60%' }} />)}
			</Form.Item>
			<Form.Item {...formItemLayout} label="Type" required={false}>
				{form.getFieldDecorator(`tasks-${taskKey}-type`, {
					validateTrigger: ['onChange'],
					initialValue:
						nullSafeGet(`tasks-${taskKey}-type`, formData) ||
						nullSafeGet(`tasks.${taskKey}.taskType`, formData),
					rules: [
						{
							required: true,
							message: 'Please choose a task type.',
						},
					],
				})(
					// TODO: Add onChange function to clear out choices and choiceKeys when changing from
					// TODO: multiple choice to another type or filter choices from other task types
					// TODO: on form validation and submission.
					<Select
						onChange={(val, opt) => updateChoiceKeys(taskKey, val, opt)}
						style={{ width: '60%' }}
					>
						<Select.Option value="multipleChoice">Multiple Choice</Select.Option>
						<Select.Option value="textbox">Free Text</Select.Option>
						{isTaskListEnabled() ? (
							<Select.Option value="taskList">Capture Tasks</Select.Option>
						) : null}
						<Select.Option value="checklist">Checklist</Select.Option>
					</Select>
				)}
			</Form.Item>
			<Form.Item
				{...formItemLayout}
				label="Follow-up actions"
				required={false}
				extra={optionLevelFollowUpsSet ? TASK_LEVEL_LOCKED_MESSAGE : undefined}
			>
				{form.getFieldDecorator(`tasks-${taskKey}-followUpActions`, {
					initialValue: nullSafeGetOrElse(`tasks.${taskKey}.followUpActions`, formData, []),
				})(
					<Select
						mode="multiple"
						placeholder={
							optionLevelFollowUpsSet ? 'Set on answer options' : 'Follow-up actions (optional)'
						}
						options={FOLLOW_UP_ACTION_OPTIONS}
						style={{ width: '60%' }}
						disabled={optionLevelFollowUpsSet}
					/>
				)}
			</Form.Item>
			{form.getFieldDecorator(`tasks-${taskKey}-orderingId`, {
				initialValue: nullSafeGetOrElse(`tasks-${taskKey}-orderingId`, formData, taskKey),
			})}
			{form.getFieldValue(`tasks-${taskKey}-type`) === 'multipleChoice' ? (
				<div>
					{form.getFieldValue(`tasks-${taskKey}-choiceKeys`).map((c, choiceIndex) => (
						<div key={`choices-${c}`}>
							{/* <div>{`Choice ${choiceIndex + 1}`}</div> */}
							{/* <div style={{ display: 'flex', flexDirection: 'row'}}> */}
							<Form.Item label={`Choice ${choiceIndex + 1}`} {...formItemLayout} required={true}>
								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'flex-start',
									}}
								>
									{form.getFieldDecorator(`tasks-${taskKey}-choices-${c}-value`, {
										validateTrigger: ['onChange', 'onBlur'],
										initialValue:
											nullSafeGet(`tasks-${taskKey}-choices-${c}-value`, formData) ||
											nullSafeGet(`tasks.${taskKey}.multipleChoicesWithLabel.${c}`, formData),
										rules: [
											{
												required: true,
												whitespace: true,
												message: 'Please enter a choice value.',
											},
										],
									})(<Input placeholder="* Enter Value" style={{ width: '30%' }} />)}

									{form.getFieldDecorator(`tasks-${taskKey}-choices-${c}-description`, {
										validateTrigger: ['onChange', 'onBlur'],
										initialValue:
											nullSafeGet(`tasks-${taskKey}-choices-${c}-description`, formData) ||
											nullSafeGet(
												`tasks.${taskKey}.multipleChoicesWithLabel.${c}.description`,
												formData
											),
										rules: [
											{
												required: false,
												whitespace: true,
											},
										],
									})(
										<Input
											placeholder="Enter Description"
											style={{ width: '30%', marginLeft: '24px' }}
										/>
									)}
									{form.getFieldDecorator(`tasks-${taskKey}-choices-${c}-followUpActions`, {
										initialValue: nullSafeGetOrElse(
											`tasks.${taskKey}.multipleChoicesWithLabel.${c}.followUpActions`,
											formData,
											[]
										),
									})(
										<Select
											mode="multiple"
											placeholder={
												taskLevelFollowUpsSet ? 'Set at task level' : 'Follow-up actions (optional)'
											}
											options={FOLLOW_UP_ACTION_OPTIONS}
											style={{ width: '30%', marginLeft: '24px' }}
											disabled={taskLevelFollowUpsSet}
											title={taskLevelFollowUpsSet ? OPTION_LEVEL_LOCKED_MESSAGE : undefined}
										/>
									)}
									{form.getFieldValue(`tasks-${taskKey}-choiceKeys`).length > 1 ? (
										<MinusCircleOutlined
											className="dynamic-delete-button"
											onClick={() => removeChoice(taskKey, c)}
										/>
									) : null}
								</div>
							</Form.Item>
							{/* </div> */}
						</div>
					))}
					<Form.Item {...formItemLayoutWithOutLabel}>
						<Button type="dashed" onClick={() => addChoice(taskKey)} style={{ width: '60%' }}>
							<PlusOutlined /> Add choice
						</Button>
					</Form.Item>
				</div>
			) : null}
			{form.getFieldValue(`tasks-${taskKey}-type`) === SITE_SURVEY_ANSWER_TYPE.CHECKLIST ? (
				<div>
					{form.getFieldValue(`tasks-${taskKey}-choiceKeys`).map((c, choiceIndex) => (
						<div key={`checklistItems-${c}`}>
							<Form.Item label={`Item ${choiceIndex + 1}`} {...formItemLayout} required={true}>
								<div className="flex flex-row items-start">
									{form.getFieldDecorator(`tasks-${taskKey}-checklistItems-${c}-title`, {
										validateTrigger: ['onChange', 'onBlur'],
										initialValue:
											nullSafeGet(`tasks-${taskKey}-checklistItems-${c}-title`, formData) ||
											nullSafeGet(`tasks.${taskKey}.checklistTasks.${c}.title`, formData),
										rules: [
											{
												required: true,
												whitespace: true,
												message: 'Please enter an item title.',
											},
										],
									})(<Input placeholder="* Enter Item" style={{ width: '60%' }} />)}
									{form.getFieldValue(`tasks-${taskKey}-choiceKeys`).length > 1 ? (
										<MinusCircleOutlined
											className="dynamic-delete-button"
											onClick={() => removeChoice(taskKey, c)}
										/>
									) : null}
								</div>
							</Form.Item>
						</div>
					))}
					<Form.Item {...formItemLayoutWithOutLabel}>
						<Button type="dashed" onClick={() => addChoice(taskKey)} style={{ width: '60%' }}>
							<PlusOutlined /> Add item
						</Button>
					</Form.Item>
				</div>
			) : null}
		</div>
	);
};

export default AddTask;
